import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import AdminLayout from "../../Layout/AdminLayout";
import API from "../../Api/Api";
import endpoints from "../../Api/Endpoints";
import { getAdminSocket } from "../../Api/socket";
import { getAdminApiBaseUrl } from "../../Api/baseUrl";
import { IoCheckmark, IoCheckmarkDone } from "react-icons/io5";
import { formatWaChatTime } from "../../utils/formatWaChatTime";
import "./SupportLive.scss";

const TEAL = "#08a996";

/** WhatsApp-style ticks for admin thread (react-icons; website uses lucide). */
function SupportLiveDeliveryTicks({ status }) {
  const Icon = status === "sent" ? IoCheckmark : IoCheckmarkDone;
  const label = status === "read" ? "Read" : status === "delivered" ? "Delivered" : "Sent";
  return (
    <span className={`support-live__wa-ticks support-live__wa-ticks--${status}`.trim()} role="img" aria-label={label}>
      <Icon size={15} />
    </span>
  );
}

function formatLastSeen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "—";
  const now = new Date();
  const diffMs = now - d;
  if (diffMs < 60000) return "Just now";
  if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m ago`;
  if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)}h ago`;
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function formatDob(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function patientAvatarUrl(avatar) {
  const a = String(avatar || "").trim();
  if (!a || a === "<default>") return "";
  const base = getAdminApiBaseUrl().replace(/\/$/, "");
  if (/^https?:\/\//i.test(a)) return a;
  if (a.startsWith("/")) return `${base}${a}`;
  return `${base}/${a.replace(/^\//, "")}`;
}

function initialsFromName(name, fallbackId) {
  const s = String(name || "").trim();
  if (s) {
    const parts = s.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase().slice(0, 2);
    return s.slice(0, 2).toUpperCase();
  }
  const id = String(fallbackId || "").replace(/[^a-f0-9]/gi, "");
  return id.slice(0, 2).toUpperCase() || "?";
}

function mergeInboxThreads(prev, threads) {
  if (!Array.isArray(threads)) return prev;
  const next = { ...prev };
  for (const t of threads) {
    if (!t?.patientId) continue;
    const pid = String(t.patientId);
    const incomingAt = Date.parse(t.lastAt) || 0;
    const prevAt = Date.parse(next[pid]?.lastAt) || 0;
    if (!next[pid] || incomingAt >= prevAt) {
      next[pid] = {
        preview: t.preview || "",
        lastAt: t.lastAt || "",
        lastFrom: t.lastFrom || "",
      };
    }
  }
  return next;
}

const SUPPORT_SEEN_STORAGE = "hb_support_admin_seen_v1";

function loadSeenBoundaryMap() {
  try {
    const raw = localStorage.getItem(SUPPORT_SEEN_STORAGE);
    const o = raw ? JSON.parse(raw) : {};
    if (!o || typeof o !== "object" || Array.isArray(o)) return {};
    const out = {};
    for (const [k, v] of Object.entries(o)) {
      const n = Number(v);
      if (Number.isFinite(n) && n > 0) out[k] = n;
    }
    return out;
  } catch {
    return {};
  }
}

/** Index in thread of the last admin message the patient marked as read (-1 if none). */
function indexOfPatientReadCut(thread, patientReadUpTo, selectedPatientId) {
  const rid = patientReadUpTo?.[selectedPatientId]?.lastMessageId;
  if (!rid) return -1;
  return thread.findIndex((m) => m.id === rid);
}

export default function SupportLive() {
  const adminAccessToken = useSelector((s) => s.auth?.accessToken);
  const hasAdminSession = useMemo(() => {
    if (String(adminAccessToken || "").trim()) return true;
    try {
      return Boolean(String(localStorage.getItem("adminToken") || "").trim());
    } catch (_) {
      return false;
    }
  }, [adminAccessToken]);
  const [messages, setMessages] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [draft, setDraft] = useState("");
  const [socketConnected, setSocketConnected] = useState(false);
  /** Last socket handshake / transport error (shown when badge is Offline). */
  const [socketDiag, setSocketDiag] = useState("");
  const typingClearTimersRef = useRef({});
  const [patientTyping, setPatientTyping] = useState(() => ({}));
  const [supportPresence, setSupportPresence] = useState(() => ({}));
  /** Last activity per patient from DB (inbox snapshot), merged with live messages and presence for the queue. */
  const [inboxThreads, setInboxThreads] = useState(() => ({}));
  const seenIds = useRef(new Set());
  const threadEndRef = useRef(null);
  const adminTypingTimerRef = useRef(null);
  const lastSelectedPatientRef = useRef("");
  const readEmitTimerRef = useRef(null);

  /** Max message time the admin has opened this thread to (for unread badges); persisted. */
  const [seenBoundaryByPatient, setSeenBoundaryByPatient] = useState(() => loadSeenBoundaryMap());
  /** Last care-team message id the patient acknowledged (read receipts on admin bubbles). */
  const [patientReadUpTo, setPatientReadUpTo] = useState(() => ({}));

  const patientIds = useMemo(() => {
    const map = new Map();
    const bump = (pid, ts) => {
      const id = String(pid || "").trim();
      if (!id) return;
      const t = Number(ts);
      if (!Number.isFinite(t) || t <= 0) return;
      const cur = map.get(id);
      if (!cur || t > cur) map.set(id, t);
    };
    for (const m of messages) {
      if (!m.patientId) continue;
      bump(m.patientId, new Date(m.createdAt || 0).getTime());
    }
    for (const [pid, meta] of Object.entries(inboxThreads)) {
      bump(pid, new Date(meta.lastAt || 0).getTime());
    }
    // Patients who open live support emit presence before a new message; include them so admins can open history.
    for (const [pid, pres] of Object.entries(supportPresence)) {
      const online = !!pres?.online;
      const lastSeen = pres?.lastSeenAt ? new Date(pres.lastSeenAt).getTime() : 0;
      bump(pid, online ? Math.max(lastSeen, Date.now()) : lastSeen);
    }
    return [...map.entries()].sort((a, b) => b[1] - a[1]).map(([id]) => id);
  }, [messages, inboxThreads, supportPresence]);

  const thread = useMemo(() => {
    if (!selectedPatientId) return [];
    const sel = String(selectedPatientId);
    return messages.filter((m) => String(m.patientId) === sel);
  }, [messages, selectedPatientId]);

  const unreadForPatient = useCallback(
    (pid) => {
      const id = String(pid || "");
      if (!id || id === String(selectedPatientId || "")) return 0;
      const b = Number(seenBoundaryByPatient[id]) || 0;
      return messages.filter(
        (m) =>
          String(m.patientId) === id &&
          m.from === "user" &&
          new Date(m.createdAt || 0).getTime() > b
      ).length;
    },
    [messages, seenBoundaryByPatient, selectedPatientId]
  );

  const patientReadCutIdx = useMemo(
    () => indexOfPatientReadCut(thread, patientReadUpTo, selectedPatientId),
    [thread, patientReadUpTo, selectedPatientId]
  );

  const selectedPatientMeta = useMemo(() => {
    if (!selectedPatientId) return null;
    const fromPresence = supportPresence[selectedPatientId]?.patient;
    const lastWithPatient = [...thread].reverse().find((m) => m.patient && typeof m.patient === "object");
    const fromMsg = lastWithPatient?.patient;
    return { id: selectedPatientId, ...(fromMsg || {}), ...(fromPresence || {}) };
  }, [selectedPatientId, supportPresence, thread]);

  const selectedOnline = supportPresence[selectedPatientId]?.online;
  const selectedLastSeen =
    supportPresence[selectedPatientId]?.lastSeenAt ||
    selectedPatientMeta?.lastSeenAt ||
    null;

  const mergeSupportHistoryIntoAppState = useCallback((pid, list) => {
    const p = String(pid || "");
    if (!p || !Array.isArray(list)) return;

    const presencePatches = {};
    for (const m of list) {
      if (!m?.id || m.text == null) continue;
      seenIds.current.add(m.id);
      if (m.from === "user" && m.patient && m.patientId) {
        const pp = String(m.patientId);
        presencePatches[pp] = { patient: m.patient };
      }
    }
    if (Object.keys(presencePatches).length) {
      setSupportPresence((sp) => {
        const next = { ...sp };
        for (const [key, patch] of Object.entries(presencePatches)) {
          next[key] = {
            ...(next[key] || {}),
            patient: { ...(next[key]?.patient || {}), ...patch.patient },
          };
        }
        return next;
      });
    }

    setMessages((prev) => {
      const others = prev.filter((m) => String(m.patientId) !== p);
      const prevForPid = prev.filter((m) => String(m.patientId) === p);
      const byId = new Map();
      for (const m of prevForPid) byId.set(m.id, m);
      for (const m of list) {
        if (!m?.id || m.text == null) continue;
        byId.set(m.id, m);
      }
      const merged = [...others, ...byId.values()];
      merged.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      return merged;
    });
  }, []);

  const appendMessage = useCallback((msg) => {
    if (!msg?.id || seenIds.current.has(msg.id)) return;
    seenIds.current.add(msg.id);
    setMessages((prev) => [...prev, msg]);
    if (msg.patientId && msg.patient && typeof msg.patient === "object") {
      setSupportPresence((prev) => ({
        ...prev,
        [msg.patientId]: {
          ...(prev[msg.patientId] || {}),
          patient: { ...(prev[msg.patientId]?.patient || {}), ...msg.patient },
        },
      }));
    }
    if (msg.from === "user" && msg.patientId) {
      const pid = String(msg.patientId);
      setSupportPresence((prev) => ({
        ...prev,
        [pid]: { ...(prev[pid] || {}), online: true, lastSeenAt: msg.createdAt || prev[pid]?.lastSeenAt },
      }));
    }
  }, []);

  const setPatientTypingFor = useCallback((patientId, isTyping) => {
    const pid = String(patientId || "");
    if (!pid) return;
    if (typingClearTimersRef.current[pid]) {
      window.clearTimeout(typingClearTimersRef.current[pid]);
      delete typingClearTimersRef.current[pid];
    }
    setPatientTyping((prev) => ({ ...prev, [pid]: !!isTyping }));
    if (isTyping) {
      typingClearTimersRef.current[pid] = window.setTimeout(() => {
        setPatientTyping((p) => ({ ...p, [pid]: false }));
        delete typingClearTimersRef.current[pid];
      }, 4500);
    }
  }, []);

  /** While this thread is open, advance “seen” so other rows’ unread counts stay accurate. */
  useEffect(() => {
    if (!selectedPatientId) return;
    const maxTs = thread.reduce((mx, m) => {
      const t = new Date(m.createdAt || 0).getTime();
      return Number.isFinite(t) ? Math.max(mx, t) : mx;
    }, 0);
    if (maxTs <= 0) return;
    setSeenBoundaryByPatient((prev) => {
      const cur = Number(prev[selectedPatientId]) || 0;
      if (maxTs <= cur) return prev;
      const next = { ...prev, [selectedPatientId]: maxTs };
      try {
        localStorage.setItem(SUPPORT_SEEN_STORAGE, JSON.stringify(next));
      } catch (_) {}
      return next;
    });
  }, [selectedPatientId, thread]);

  useEffect(() => {
    const socket = getAdminSocket(adminAccessToken);
    if (!socket) {
      setSocketConnected(false);
      setSocketDiag("");
      return undefined;
    }

    const syncConn = () => {
      setSocketConnected(Boolean(socket.connected));
      if (socket.connected) setSocketDiag("");
    };
    const onConnectErr = (err) => {
      setSocketConnected(false);
      const msg =
        err && typeof err === "object" && "message" in err
          ? String(err.message || "")
          : String(err || "");
      setSocketDiag(msg || "Could not connect to the server. Check API URL and admin login.");
    };

    socket.on("connect", syncConn);
    socket.on("disconnect", syncConn);
    socket.on("connect_error", onConnectErr);
    syncConn();

    return () => {
      try {
        socket.off("connect", syncConn);
        socket.off("disconnect", syncConn);
        socket.off("connect_error", onConnectErr);
      } catch (_) {}
    };
  }, [adminAccessToken]);

  useEffect(() => {
    const socket = getAdminSocket(adminAccessToken);
    if (!socket) return undefined;

    const onSupportMessage = (msg) => {
      if (!msg || typeof msg !== "object") return;
      if (!msg.id || !msg.patientId || msg.text == null) return;
      appendMessage(msg);
    };

    const onSupportPresence = (payload) => {
      if (!payload?.patientId) return;
      const pid = String(payload.patientId);
      const state = String(payload.state || "").toLowerCase();
      const online = state === "online";
      setSupportPresence((prev) => ({
        ...prev,
        [pid]: {
          ...(prev[pid] || {}),
          online,
          lastSeenAt: payload.lastSeenAt || prev[pid]?.lastSeenAt || null,
          ...(payload.patient && typeof payload.patient === "object"
            ? { patient: { ...(prev[pid]?.patient || {}), ...payload.patient } }
            : {}),
        },
      }));
    };

    const onSupportTyping = (payload) => {
      if (!payload || payload.from !== "patient" || !payload.patientId) return;
      setPatientTypingFor(payload.patientId, !!payload.isTyping);
    };

    const onSupportHistory = (payload) => {
      const pid = String(payload?.patientId || "");
      const list = payload?.messages;
      if (!pid || !Array.isArray(list)) return;
      mergeSupportHistoryIntoAppState(pid, list);
    };

    const onSupportAdminInbox = (payload) => {
      const threads = payload?.threads;
      if (!Array.isArray(threads)) return;
      setInboxThreads((prev) => mergeInboxThreads(prev, threads));
    };

    /** Server sends patients already in live support when this admin tab connects (missed earlier presence). */
    const onSupportLivePatients = (payload) => {
      const list = payload?.patients;
      if (!Array.isArray(list) || list.length === 0) return;
      setSupportPresence((prev) => {
        const next = { ...prev };
        for (const row of list) {
          const pid = String(row?.patientId || "").trim();
          if (!pid) continue;
          next[pid] = {
            ...(next[pid] || {}),
            online: row.online !== false,
            lastSeenAt: row.lastSeenAt || next[pid]?.lastSeenAt || new Date().toISOString(),
            ...(row.patient && typeof row.patient === "object"
              ? { patient: { ...(next[pid]?.patient || {}), ...row.patient } }
              : {}),
          };
        }
        return next;
      });
    };

    const onSupportPatientRead = (payload) => {
      const pid = String(payload?.patientId || "");
      if (!pid) return;
      setPatientReadUpTo((prev) => ({
        ...prev,
        [pid]: {
          lastMessageId: String(payload?.lastMessageId || ""),
          readAt: payload?.readAt || new Date().toISOString(),
        },
      }));
    };

    const onJoined = () => {
      try {
        socket.emit("support:join");
      } catch (_) {}
    };

    socket.on("support:message", onSupportMessage);
    socket.on("support:presence", onSupportPresence);
    socket.on("support:typing", onSupportTyping);
    socket.on("support:history", onSupportHistory);
    socket.on("support:adminInbox", onSupportAdminInbox);
    socket.on("support:livePatients", onSupportLivePatients);
    socket.on("support:patientRead", onSupportPatientRead);
    socket.on("connect", onJoined);
    if (socket.connected) onJoined();

    return () => {
      socket.off("support:message", onSupportMessage);
      socket.off("support:presence", onSupportPresence);
      socket.off("support:typing", onSupportTyping);
      socket.off("support:history", onSupportHistory);
      socket.off("support:adminInbox", onSupportAdminInbox);
      socket.off("support:livePatients", onSupportLivePatients);
      socket.off("support:patientRead", onSupportPatientRead);
      socket.off("connect", onJoined);
    };
  }, [appendMessage, adminAccessToken, mergeSupportHistoryIntoAppState, setPatientTypingFor]);

  /** Restore queue from DB on load/refresh (Socket.IO can arrive late or miss the first packet). */
  useEffect(() => {
    if (!hasAdminSession) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await API.get(endpoints.ADMIN_SUPPORT_INBOX);
        const threads = res?.data?.threads;
        if (!Array.isArray(threads) || cancelled) return;
        setInboxThreads((prev) => mergeInboxThreads(prev, threads));
      } catch (_) {}
    })();
    return () => {
      cancelled = true;
    };
  }, [hasAdminSession]);

  /** Restore open thread from DB on load/refresh and when switching patients. */
  useEffect(() => {
    if (!hasAdminSession || !selectedPatientId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await API.get(endpoints.adminSupportHistory(selectedPatientId), {
          params: { limit: 250 },
        });
        const list = res?.data?.messages;
        const pid = String(res?.data?.patientId || selectedPatientId);
        if (!Array.isArray(list) || cancelled) return;
        mergeSupportHistoryIntoAppState(pid, list);
      } catch (_) {}
    })();
    return () => {
      cancelled = true;
    };
  }, [hasAdminSession, selectedPatientId, mergeSupportHistoryIntoAppState]);

  useEffect(() => {
    if (!socketConnected || !selectedPatientId) return undefined;
    const socket = getAdminSocket(adminAccessToken);
    if (!socket?.connected) return undefined;
    try {
      socket.emit("support:fetchHistory", { patientId: selectedPatientId, limit: 250 });
    } catch (_) {}
    return undefined;
  }, [selectedPatientId, socketConnected, adminAccessToken]);

  /** Tell the patient app the admin is viewing (read receipt / “seen” on their side) in real time. */
  useEffect(() => {
    if (!socketConnected || !selectedPatientId) return undefined;
    if (readEmitTimerRef.current) window.clearTimeout(readEmitTimerRef.current);
    readEmitTimerRef.current = window.setTimeout(() => {
      const socket = getAdminSocket(adminAccessToken);
      if (!socket?.connected) return;
      const last = thread[thread.length - 1];
      try {
        socket.emit("support:read", {
          patientId: selectedPatientId,
          readAt: new Date().toISOString(),
          lastMessageId: last?.id || "",
        });
      } catch (_) {}
    }, 450);
    return () => {
      if (readEmitTimerRef.current) window.clearTimeout(readEmitTimerRef.current);
    };
  }, [selectedPatientId, thread, socketConnected, adminAccessToken]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView?.({ behavior: "smooth", block: "end" });
  }, [thread, selectedPatientId, patientTyping]);

  useEffect(() => {
    if (patientIds.length === 0) {
      setSelectedPatientId("");
      return;
    }
    if (!selectedPatientId || !patientIds.includes(selectedPatientId)) {
      setSelectedPatientId(patientIds[0]);
    }
  }, [patientIds, selectedPatientId]);

  useEffect(() => {
    const socket = getAdminSocket(adminAccessToken);
    const prev = lastSelectedPatientRef.current;
    if (prev && prev !== selectedPatientId && socket?.connected) {
      try {
        socket.emit("support:typing", { patientId: prev, isTyping: false });
      } catch (_) {}
    }
    lastSelectedPatientRef.current = selectedPatientId || "";
  }, [selectedPatientId, adminAccessToken]);

  useEffect(() => {
    return () => {
      const socket = getAdminSocket(adminAccessToken);
      const pid = lastSelectedPatientRef.current;
      if (socket?.connected && pid) {
        try {
          socket.emit("support:typing", { patientId: pid, isTyping: false });
        } catch (_) {}
      }
    };
  }, [adminAccessToken]);

  useEffect(() => {
    const socket = getAdminSocket(adminAccessToken);
    if (!socket?.connected || !selectedPatientId) return undefined;

    const emit = (isTyping) => {
      try {
        socket.emit("support:typing", { patientId: selectedPatientId, isTyping: !!isTyping });
      } catch (_) {}
    };

    if (adminTypingTimerRef.current) window.clearTimeout(adminTypingTimerRef.current);
    adminTypingTimerRef.current = window.setTimeout(() => {
      emit(!!draft.trim());
    }, 220);

    return () => {
      if (adminTypingTimerRef.current) window.clearTimeout(adminTypingTimerRef.current);
    };
  }, [draft, selectedPatientId, socketConnected, adminAccessToken]);

  const sendReply = (e) => {
    e.preventDefault();
    const text = String(draft || "").trim();
    const pid = String(selectedPatientId || "").trim();
    if (!text || !pid) return;
    const socket = getAdminSocket(adminAccessToken);
    if (!socket?.connected) return;
    try {
      socket.emit("support:typing", { patientId: pid, isTyping: false });
    } catch (_) {}
    socket.emit("support:message", { patientId: pid, text });
    setDraft("");
  };

  const canSend = Boolean(selectedPatientId && draft.trim() && socketConnected);

  const queueLabel = (pid) => {
    const meta = supportPresence[pid]?.patient;
    const name = meta?.name && String(meta.name).trim();
    if (name) return name;
    const fromMsg = [...messages].reverse().find((m) => m.patientId === pid && m.patient?.name);
    if (fromMsg?.patient?.name) return String(fromMsg.patient.name).trim();
    return `${String(pid).slice(0, 10)}…`;
  };

  return (
    <AdminLayout>
      <div className="support-live">
        <div className="support-live__header">
          <div className="support-live__header-row">
            <h1>Patient support — live chat</h1>
            <span
              className={`support-live__conn ${socketConnected ? "is-on" : "is-off"}`}
              title={
                socketConnected
                  ? "Socket connected"
                  : socketDiag || "Socket disconnected — log in or check that the API URL matches your backend."
              }
            >
              {socketConnected ? "Connected" : "Offline"}
            </span>
          </div>
          {!hasAdminSession ? (
            <div className="support-live__banner" role="status">
              <strong>No admin session.</strong> This page needs a valid admin JWT (same as the dashboard).{" "}
              <Link to="/" className="support-live__banner-link">
                Open the login page
              </Link>
              , sign in, then open Live patient chat again.
            </div>
          ) : null}
          {hasAdminSession && !socketConnected && socketDiag ? (
            <p className="support-live__diag">{socketDiag}</p>
          ) : null}
          <p className="support-live__sub">
            Real time over Socket.IO: online / last seen, typing from either side, message delivery to the server,
            unread counts in the queue (after you have opened a thread once on this browser), and read receipts when
            the patient has seen your replies. Refresh restores saved threads from the API; live cues still need the
            socket connected.
          </p>
        </div>

        <div className="support-live__grid">
          <aside className="support-live__sidebar">
            <div className="support-live__sidebar-title">Conversation queue</div>
            {patientIds.length === 0 && (
              <div className="support-live__empty">
                No support threads yet. When a patient opens live chat or sends a message, they appear here; choose
                them to load saved history from the server.
              </div>
            )}
            <ul className="support-live__list">
              {patientIds.map((id) => {
                const last = [...messages].reverse().find((m) => String(m.patientId) === id);
                const inbox = inboxThreads[id];
                const preview = last?.text
                  ? String(last.text).slice(0, 52)
                  : inbox?.preview
                    ? String(inbox.preview).slice(0, 52)
                    : "—";
                const online = supportPresence[id]?.online;
                const unread = unreadForPatient(id);
                return (
                  <li key={id}>
                    <button
                      type="button"
                      className={`support-live__patient ${selectedPatientId === id ? "is-active" : ""}`}
                      onClick={() => setSelectedPatientId(id)}
                    >
                      <span className="support-live__patient-row">
                        <span className={`support-live__dot${online ? " is-on" : ""}`} title={online ? "Online" : "Offline"} aria-hidden />
                        <span className="support-live__patient-main">
                          <span className="support-live__patient-name-row">
                            <span className="support-live__patient-name">{queueLabel(id)}</span>
                            {unread > 0 ? (
                              <span className="support-live__unread" aria-label={`${unread} unread`}>
                                {unread > 99 ? "99+" : unread}
                              </span>
                            ) : null}
                          </span>
                          <span className="support-live__patient-id">{id.slice(0, 12)}…</span>
                          <span className="support-live__preview">{preview}</span>
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          <section className="support-live__main">
            {!selectedPatientId ? (
              <div className="support-live__placeholder">Choose a patient from the queue.</div>
            ) : (
              <>
                <div className="support-live__patient-card">
                  <div className="support-live__avatar-wrap">
                    {patientAvatarUrl(selectedPatientMeta?.avatar) ? (
                      <img
                        className="support-live__avatar-img"
                        src={patientAvatarUrl(selectedPatientMeta?.avatar)}
                        alt=""
                      />
                    ) : (
                      <span className="support-live__avatar-fallback" aria-hidden>
                        {initialsFromName(selectedPatientMeta?.name, selectedPatientId)}
                      </span>
                    )}
                  </div>
                  <div className="support-live__patient-card-body">
                    <div className="support-live__patient-card-top">
                      <h2 className="support-live__patient-title">
                        {selectedPatientMeta?.name?.trim() || "Patient"}
                      </h2>
                      <span className={`support-live__pill${selectedOnline ? " is-on" : " is-off"}`}>
                        {selectedOnline ? "Online" : "Offline"}
                      </span>
                    </div>
                    <p className="support-live__patient-sub">
                      Last seen: {formatLastSeen(selectedLastSeen)}
                      {patientTyping[selectedPatientId] ? (
                        <span className="support-live__typing"> · Patient is typing…</span>
                      ) : null}
                    </p>
                    <dl className="support-live__detail-grid">
                      <div>
                        <dt>Patient ID</dt>
                        <dd title={selectedPatientId}>{selectedPatientId}</dd>
                      </div>
                      <div>
                        <dt>Mobile</dt>
                        <dd>{selectedPatientMeta?.mobile || "—"}</dd>
                      </div>
                      <div>
                        <dt>Gender</dt>
                        <dd>{selectedPatientMeta?.gender || "—"}</dd>
                      </div>
                      <div>
                        <dt>Blood group</dt>
                        <dd>{selectedPatientMeta?.bloodGroup || "—"}</dd>
                      </div>
                      <div>
                        <dt>Date of birth</dt>
                        <dd>{formatDob(selectedPatientMeta?.dateOfBirth) || "—"}</dd>
                      </div>
                      <div>
                        <dt>Profile</dt>
                        <dd>{selectedPatientMeta?.isProfileComplete ? "Complete" : "Incomplete"}</dd>
                      </div>
                    </dl>
                  </div>
                </div>

                <div className="support-live__thread">
                  {thread.map((m, idx) => {
                    const waTime = formatWaChatTime(m.createdAt);
                    const adminRead =
                      m.from === "admin" && patientReadCutIdx >= 0 && idx <= patientReadCutIdx;
                    return (
                      <div
                        key={m.id}
                        className={`support-live__bubble ${m.from === "admin" ? "from-admin" : "from-user"}`}
                      >
                        <div className="support-live__bubble-body">
                          <div className="support-live__text">{m.text}</div>
                          <div className="support-live__bubble-meta">
                            <span className="support-live__bubble-time">{waTime}</span>
                            {m.from === "admin" ? (
                              <span aria-live="polite">
                                <SupportLiveDeliveryTicks
                                  status={adminRead ? "read" : "delivered"}
                                />
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {patientTyping[selectedPatientId] ? (
                    <div className="support-live__typing-bubble" aria-live="polite">
                      Patient is typing…
                    </div>
                  ) : null}
                  <div ref={threadEndRef} />
                </div>
                <form className="support-live__composer" onSubmit={sendReply}>
                  <input
                    className="support-live__input"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={socketConnected ? "Type a reply…" : "Reconnect to send…"}
                    autoComplete="off"
                    disabled={!socketConnected}
                  />
                  <button type="submit" className="support-live__send" style={{ background: TEAL }} disabled={!canSend}>
                    Send
                  </button>
                </form>
              </>
            )}
          </section>
        </div>
      </div>
    </AdminLayout>
  );
}
